import { socketClient } from "./socketClient";
import { SocketEvents } from "./events";

type Listener<T> = (payload: T) => void;

// Generic helper to attach and detach listeners
const createListener = <T>(event: SocketEvents) => {
  return (callback: Listener<T>) => {
    socketClient.on(event, callback);
    // Return a cleanup function
    return () => {
      socketClient.off(event, callback);
    };
  };
};

// ==========================================
// Call Initiation Listeners
// ==========================================

export const onCallInitiated = createListener<SocketTypes.CallInitiatedPayload>(
  SocketEvents.CALL_INITIATED
);

export const onCallIncoming = createListener<SocketTypes.CallIncomingPayload>(
  SocketEvents.CALL_INCOMING
);

export const onCallInitiateError = createListener<SocketTypes.CallErrorPayload>(
  SocketEvents.CALL_INITIATE_ERROR
);

// ==========================================
// Call Acceptance Listeners
// ==========================================

export const onCallAccepted = createListener<SocketTypes.CallAcceptedPayload>(
  SocketEvents.CALL_ACCEPTED
);

export const onCallAcceptedByRecipient =
  createListener<SocketTypes.CallAcceptedPayload>(
    SocketEvents.CALL_ACCEPTED_BY_RECIPIENT
  );

export const onCallAcceptError = createListener<SocketTypes.CallErrorPayload>(
  SocketEvents.CALL_ACCEPT_ERROR
);

// ==========================================
// Call Rejection Listeners
// ==========================================

export const onCallRejected = createListener<SocketTypes.CallRejectedPayload>(
  SocketEvents.CALL_REJECTED
);

export const onCallRejectedByRecipient =
  createListener<SocketTypes.CallRejectedByRecipientPayload>(
    SocketEvents.CALL_REJECTED_BY_RECIPIENT
  );

export const onCallRejectedConfirmed =
  createListener<SocketTypes.CallRejectedConfirmedPayload>(
    SocketEvents.CALL_REJECTED_CONFIRMED
  );

export const onCallRejectError = createListener<SocketTypes.CallErrorPayload>(
  SocketEvents.CALL_REJECT_ERROR
);

// ==========================================
// Call Cancellation Listeners
// ==========================================

export const onCallCancelled = createListener<SocketTypes.CallCancelledPayload>(
  SocketEvents.CALL_CANCELLED
);

export const onCallCancelledByInitiator =
  createListener<SocketTypes.CallCancelledByInitiatorPayload>(
    SocketEvents.CALL_CANCELLED_BY_INITIATOR
  );

export const onCallCancelError = createListener<SocketTypes.CallErrorPayload>(
  SocketEvents.CALL_CANCEL_ERROR
);

// ==========================================
// Call Connection Listeners
// ==========================================

export const onCallMediaStarted =
  createListener<SocketTypes.CallMediaStartedPayload>(
    SocketEvents.CALL_MEDIA_STARTED
  );

export const onCallConnectedConfirmed =
  createListener<SocketTypes.CallConnectedConfirmedPayload>(
    SocketEvents.CALL_CONNECTED_CONFIRMED
  );

export const onCallConnectedError =
  createListener<SocketTypes.CallErrorPayload>(
    SocketEvents.CALL_CONNECTED_ERROR
  );

// ==========================================
// Call Termination Listeners
// ==========================================

export const onCallEnded = createListener<SocketTypes.CallEndedPayload>(
  SocketEvents.CALL_ENDED
);

export const onCallEndError = createListener<SocketTypes.CallErrorPayload>(
  SocketEvents.CALL_END_ERROR
);

// ==========================================
// Timeout/Missed Listeners
// ==========================================

export const onCallMissed = createListener<SocketTypes.CallMissedPayload>(
  SocketEvents.CALL_MISSED
);

export const onCallTimeout = createListener<SocketTypes.CallTimeoutPayload>(
  SocketEvents.CALL_TIMEOUT
);

// ==========================================
// Call Recovery Listeners
// ==========================================

export const onCallRejoinResponse =
  createListener<SocketTypes.CallRejoinResponse>(
    SocketEvents.CALL_REJOIN_RESPONSE
  );

// ==========================================
// Admin Monitoring Listeners (ADMIN role only)
// ==========================================

export const onAdminJoinedCall =
  createListener<SocketTypes.AdminJoinedCallPayload>(
    SocketEvents.ADMIN_JOINED_CALL
  );

export const onAdminJoinCallError =
  createListener<SocketTypes.CallErrorPayload>(
    SocketEvents.ADMIN_JOIN_CALL_ERROR
  );

export const onAdminUiSync = createListener<SocketTypes.AdminUiSyncPayload>(
  SocketEvents.ADMIN_UI_SYNC
);

// ==========================================
// Room/Participant Listeners
// ==========================================

export const onParticipantJoined =
  createListener<SocketTypes.ParticipantPayload>(
    SocketEvents.PARTICIPANT_JOINED
  );

export const onParticipantLeft = createListener<SocketTypes.ParticipantPayload>(
  SocketEvents.PARTICIPANT_LEFT
);

export const onMediaUpdated = createListener<SocketTypes.MediaUpdatedPayload>(
  SocketEvents.MEDIA_UPDATED
);

export const onUserStatusUpdate =
  createListener<SocketTypes.UserStatusPayload>(SocketEvents.UPDATE_USER_STATUS);

// ==========================================
// Connection Listeners
// ==========================================

export const onConnect = (callback: () => void) => {
  socketClient.on(SocketEvents.CONNECT, callback);
  return () => socketClient.off(SocketEvents.CONNECT, callback);
};

export const onDisconnect = (callback: (reason: string) => void) => {
  socketClient.on(SocketEvents.DISCONNECT, callback);
  return () => socketClient.off(SocketEvents.DISCONNECT, callback);
};

